import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import { CaseItem } from "./types"; // Assuming CaseItem is defined elsewhere

interface CaseContextType {
  caseData: CaseItem[];
  setCaseData: React.Dispatch<React.SetStateAction<CaseItem[]>>;
  fetchCaseData: (page?: number) => Promise<void>;
  currentPage: number;
  totalPages: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
}

interface CaseProviderProps {
  children: ReactNode;
}

// Define CasePageData interface to match the response data structure
interface CasePageData {
  cases: CaseItem[];
  currentPage: number;
  totalPages: number;
}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;

export const CaseProvider: React.FC<CaseProviderProps> = ({ children }) => {
  const [caseData, setCaseData] = useState<CaseItem[]>(() => {
    const savedData = sessionStorage.getItem("caseData");
    return savedData ? JSON.parse(savedData) : [];
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("currentPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [totalPages, setTotalPages] = useState(() => {
    const savedTotalPages = sessionStorage.getItem("totalPages");
    return savedTotalPages ? parseInt(savedTotalPages, 10) : 1;
  });

  const saveToSessionStorage = (key: string, value: any) => {
    sessionStorage.setItem(key, JSON.stringify(value));
  };

  // Define fetchPageData function with proper return type
  const fetchPageData = async (page: number): Promise<CasePageData> => {
    const authToken = sessionStorage.getItem("token");
    const response = await fetch(
      `https://backend-kyw8.onrender.com/my-cases?page=${page}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching page ${page}`);
    }

    // Cast response to CasePageData
    const data: CasePageData = await response.json();
    return data;
  };

  // Fetch case data function
  const fetchCaseData = useCallback(
    async (retries = 0): Promise<void> => {
      const authToken = sessionStorage.getItem("token");

      try {
        setLoading(true);

        const initialResponse = await fetch(
          `https://backend-kyw8.onrender.com/my-cases?page=1`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!initialResponse.ok) {
          if (initialResponse.status === 401) {
            throw new Error("Unauthorized. Please login again.");
          }
          throw new Error("Something went wrong");
        }

        const initialData: CasePageData = await initialResponse.json();
        let allCases = initialData.cases;
        const totalPages = initialData.totalPages;

        const pagePromises: Promise<CasePageData>[] = [];

        // Fetch subsequent pages
        for (let page = 2; page <= totalPages; page++) {
          pagePromises.push(fetchPageData(page));
        }

        // Wait for all pages to resolve
        const pageResults = await Promise.all(pagePromises);

        // Combine cases from all pages
        pageResults.forEach((result) => {
          allCases = allCases.concat(result.cases);
        });

        // Save and update state
        setCaseData(allCases);
        setCurrentPage(initialData.currentPage);
        setTotalPages(totalPages);
        saveToSessionStorage("caseData", allCases);
        saveToSessionStorage("currentPage", initialData.currentPage);
        saveToSessionStorage("totalPages", totalPages);
        console.log(allCases);
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error("fetchAllCaseData Error:", error.message);
          if (retries < MAX_RETRIES) {
            const delay = RETRY_DELAY * 2 ** retries;
            setTimeout(() => fetchCaseData(retries + 1), delay);
          }
        } else {
          console.error("fetchAllCaseData Error:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setCaseData, setCurrentPage, setTotalPages]
  );

  useEffect(() => {
    const authToken = sessionStorage.getItem("token");
    if (authToken) {
      fetchCaseData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   const authToken = sessionStorage.getItem('token');
  //   if (authToken) {
  //     fetchCaseData();
  //   }
  // }, []);

  return (
    <CaseContext.Provider
      value={{
        caseData,
        setCaseData,
        fetchCaseData,
        currentPage,
        totalPages,
        setCurrentPage,
        loading,
      }}
    >
      {children}
    </CaseContext.Provider>
  );
};

export const useCaseContext = () => {
  const context = useContext(CaseContext);
  if (!context) {
    throw new Error("useCaseContext must be used within a CaseProvider");
  }
  return context;
};
